import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../config/api';
import toast from 'react-hot-toast';

interface PortalCheck {
  property_address: string;
  portal: string;
  account_number: string;
  balance: number;
  violation_amount: number;
  last_payment_date: string | null;
  last_payment_amount: number | null;
  autopay_enabled: boolean;
  status: string;
  error_message: string | null;
  checked_at: string;
}

const PORTAL_URLS: Record<string, string> = {
  townsq: 'https://app.townsq.io',
  clickpay: 'https://www.clickpay.com',
  rhodesranch: 'https://commarea.cincwebaxis.com',
  nvcm: 'https://home.nvcm.com',
  vintagehills: 'https://desert.vmsclientonline.com',
  laderapark: 'https://hoa.tmilv.com',
};

const PORTAL_NAMES: Record<string, string> = {
  townsq: 'TownSq',
  clickpay: 'ClickPay',
  rhodesranch: 'Rhodes Ranch',
  nvcm: 'NVCM / Vantaca',
  vintagehills: 'Vintage Hills',
  laderapark: 'Ladera Park',
};

const fmt = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const fmtDate = (d: string | null) => {
  if (!d) return 'Never';
  return new Date(d).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

export const HOAStatus: React.FC = () => {
  const [results, setResults] = useState<PortalCheck[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [checkingPortal, setCheckingPortal] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/hoa-checker/results');
      setResults(res.data.results || []);
    } catch { /* table may not exist */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runCheck = async (portal?: string) => {
    setIsChecking(true);
    setCheckingPortal(portal || 'all');
    toast.loading(portal ? `Checking ${PORTAL_NAMES[portal] || portal}...` : 'Checking all portals...', { id: 'hoa' });
    try {
      const params = portal ? { portal } : {};
      await api.post('/hoa-checker/run', null, { params });
      toast.success('Portal check complete!', { id: 'hoa' });
      await load();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Check failed', { id: 'hoa' });
    } finally {
      setIsChecking(false);
      setCheckingPortal(null);
    }
  };

  // Group by portal
  const byPortal = results.reduce((acc, r) => {
    if (!acc[r.portal]) acc[r.portal] = [];
    acc[r.portal].push(r);
    return acc;
  }, {} as Record<string, PortalCheck[]>);

  const totalDue = results.reduce((s, r) => s + (r.balance || 0), 0);
  const totalViolations = results.reduce((s, r) => s + (r.violation_amount || 0), 0);
  const currentCount = results.filter(r => r.status === 'current').length;
  const attentionCount = results.filter(r => r.status !== 'current').length;
  const lastCheck = results.length > 0
    ? results.reduce((latest, r) => r.checked_at > latest ? r.checked_at : latest, '')
    : null;
  const attentionItems = results.filter(r => r.status !== 'current' && r.status !== 'unknown');

  const statusBorder = (s: string) => {
    switch (s) {
      case 'current': return 'border-l-4 border-l-green-500';
      case 'due': return 'border-l-4 border-l-yellow-500';
      case 'overdue': return 'border-l-4 border-l-red-500';
      case 'error': return 'border-l-4 border-l-red-500';
      case 'credit': return 'border-l-4 border-l-emerald-500';
      default: return 'border-l-4 border-l-gray-600';
    }
  };

  const statusBadge = (s: string) => {
    switch (s) {
      case 'current': return 'bg-green-500/20 text-green-400';
      case 'due': return 'bg-yellow-500/20 text-yellow-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      case 'error': return 'bg-red-500/20 text-red-400';
      case 'credit': return 'bg-emerald-500/20 text-emerald-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-mono flex items-center gap-3">
            <span className="text-cyan-400">~/</span>hoa-status
          </h1>
          <p className="text-gray-500 text-sm mt-1 font-mono">
            Balance Checker Dashboard
            <span className="ml-3 text-gray-600">Last checked: {fmtDate(lastCheck)}</span>
          </p>
        </div>
        <button
          onClick={() => runCheck()}
          disabled={isChecking}
          className="flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-sm font-semibold transition-all
            bg-gradient-to-r from-yellow-600/80 to-yellow-700/80 text-white
            hover:from-yellow-500/80 hover:to-yellow-600/80 hover:shadow-lg hover:shadow-yellow-600/20
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isChecking ? (
            <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Checking...</>
          ) : (
            <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" /></svg> Check Now</>
          )}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-yellow-600/20 rounded-xl p-5">
          <div className="text-gray-400 text-xs font-mono uppercase tracking-wide">Total Amount Due</div>
          <div className={`text-2xl font-bold font-mono mt-2 ${totalDue > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
            {fmt(totalDue)}
          </div>
        </div>
        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-green-600/15 rounded-xl p-5">
          <div className="text-gray-400 text-xs font-mono uppercase tracking-wide">Current</div>
          <div className="text-2xl font-bold font-mono mt-2 text-green-400">{currentCount}</div>
        </div>
        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-red-600/15 rounded-xl p-5">
          <div className="text-gray-400 text-xs font-mono uppercase tracking-wide">Need Attention</div>
          <div className={`text-2xl font-bold font-mono mt-2 ${attentionCount > 0 ? 'text-red-400' : 'text-gray-500'}`}>
            {attentionCount}
          </div>
        </div>
        <div className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border border-yellow-600/15 rounded-xl p-5">
          <div className="text-gray-400 text-xs font-mono uppercase tracking-wide">Violations</div>
          <div className={`text-2xl font-bold font-mono mt-2 ${totalViolations > 0 ? 'text-red-400' : 'text-gray-500'}`}>
            {fmt(totalViolations)}
          </div>
        </div>
      </div>

      {/* Attention Section */}
      {attentionItems.length > 0 && (
        <div>
          <h2 className="text-sm font-mono text-yellow-500 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-6 h-0.5 bg-yellow-500" />
            Properties Needing Attention
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {attentionItems.map((r, i) => (
              <PropertyCard key={`att-${i}`} r={r} statusBorder={statusBorder} statusBadge={statusBadge} />
            ))}
          </div>
        </div>
      )}

      {/* Portal Sections */}
      {Object.entries(byPortal).map(([portal, properties]) => (
        <div key={portal}>
          <h2 className="text-sm font-mono text-yellow-500/80 uppercase tracking-wider mb-3 flex items-center gap-2">
            <span className="w-6 h-0.5 bg-yellow-500/60" />
            {PORTAL_NAMES[portal] || portal.toUpperCase()} ({properties.length} properties)
            <button
              onClick={() => runCheck(portal)}
              disabled={isChecking}
              className="ml-auto text-[10px] font-mono text-gray-500 hover:text-gray-300 bg-gray-800/50 px-2 py-1 rounded border border-gray-700/50 hover:border-gray-600 transition-colors disabled:opacity-40"
            >
              {checkingPortal === portal ? 'Checking...' : 'Check'}
            </button>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {properties.map((r, i) => (
              <PropertyCard key={`${portal}-${i}`} r={r} statusBorder={statusBorder} statusBadge={statusBadge} />
            ))}
          </div>
        </div>
      ))}

      {results.length === 0 && !isChecking && (
        <div className="text-center py-16">
          <p className="text-gray-500 font-mono text-sm mb-4">No HOA data available yet.</p>
          <button
            onClick={() => runCheck()}
            className="px-5 py-2.5 rounded-lg font-mono text-sm font-semibold bg-gradient-to-r from-yellow-600/80 to-yellow-700/80 text-white hover:shadow-lg hover:shadow-yellow-600/20 transition-all"
          >
            Run First Check
          </button>
        </div>
      )}
    </div>
  );
};

const PropertyCard: React.FC<{
  r: PortalCheck;
  statusBorder: (s: string) => string;
  statusBadge: (s: string) => string;
}> = ({ r, statusBorder, statusBadge }) => {
  const portalUrl = PORTAL_URLS[r.portal] || '#';

  return (
    <div className={`bg-gradient-to-br from-[#1e1e23] to-[#14141a] border border-yellow-600/15 rounded-xl p-5 transition-all hover:border-yellow-500/30 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-yellow-600/10 ${statusBorder(r.status)}`}>
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <div className="font-mono text-sm font-semibold text-yellow-400/90">{r.property_address}</div>
          <div className="inline-block mt-1 px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded text-[10px] uppercase tracking-wider">
            {r.portal}
          </div>
          <div className="text-[11px] text-gray-500 mt-1">Account: {r.account_number || 'N/A'}</div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full ${statusBadge(r.status)}`}>
          {r.status}
        </span>
      </div>

      {/* Balance */}
      <div className="flex justify-between items-center py-2 border-b border-white/5">
        <span className="text-gray-400 text-sm">Balance Due</span>
        <span className={`font-mono text-lg font-bold ${r.balance > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
          {fmt(r.balance)}
        </span>
      </div>

      {/* Violations */}
      {r.violation_amount > 0 && (
        <div className="flex justify-between items-center py-2 border-b border-white/5">
          <span className="text-gray-400 text-sm">Violations</span>
          <span className="font-mono font-bold text-red-400">{fmt(r.violation_amount)}</span>
        </div>
      )}

      {/* Last Payment */}
      {r.last_payment_date && (
        <div className="flex justify-between items-center py-2 border-b border-white/5">
          <span className="text-gray-400 text-sm">Last Payment</span>
          <span className="font-mono text-sm text-gray-300">
            {r.last_payment_amount ? fmt(r.last_payment_amount) : ''} {r.last_payment_date}
          </span>
        </div>
      )}

      {/* Autopay */}
      <div className="flex justify-between items-center py-2 border-b border-white/5">
        <span className="text-gray-400 text-sm">Autopay</span>
        <span className={`text-xs font-semibold ${r.autopay_enabled ? 'text-green-400' : 'text-gray-600'}`}>
          {r.autopay_enabled ? 'ENABLED' : 'OFF'}
        </span>
      </div>

      {/* Error */}
      {r.error_message && (
        <div className="mt-2 p-2 bg-red-500/10 rounded text-red-400 text-xs font-mono">
          {r.error_message}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-between items-center mt-3">
        <a href={portalUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-400 text-xs hover:underline">
          Open Portal &rarr;
        </a>
        <span className="text-gray-600 text-[10px]">
          {r.checked_at ? fmtDate(r.checked_at) : ''}
        </span>
      </div>
    </div>
  );
};

export default HOAStatus;
